import { use, type ComponentType } from 'react';

type Loadable<M, K extends keyof M> = M[K] extends ComponentType<never> ? K : never;

type Screen = ComponentType<Record<string, unknown>>;
type TrackedPromise = Promise<Screen> & { status?: 'fulfilled'; value?: Screen };

/**
 * Componente de rota carregado sob demanda. O roteador chama `preload` antes de trocar de tela
 * (intenção e navegação), então a tela chega pronta e troca sem suspender. Se ainda não chegou,
 * quem segura é o `Suspense` do conteúdo da casca, nunca a raiz.
 */
export function lazyScreen<M, K extends keyof M = 'default' & keyof M>(load: () => Promise<M>, key?: Loadable<M, K>) {
  let promise: TrackedPromise | undefined;
  const preload = () => {
    if (!promise) {
      const tracked: TrackedPromise = load().then((mod) => {
        const screen = mod[key ?? ('default' as K)] as Screen;
        // `use()` numa promessa nativa suspende uma vez mesmo já resolvida, e a casca inteira piscava
        // na primeira visita. Com `status`/`value` (o formato que o React lê), a tela sai na hora.
        tracked.status = 'fulfilled';
        tracked.value = screen;
        return screen;
      });
      promise = tracked;
    }
    return promise;
  };
  function LazyScreen(props: Record<string, unknown>) {
    const Component = use(preload() as Promise<Screen>);
    return <Component {...props} />;
  }
  LazyScreen.preload = async () => {
    await preload();
  };
  return LazyScreen;
}
